import { useCallback, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import {
    type SuppliersSearch,
    suppliersSearchFrom,
    suppliersUrlFrom,
} from '@/features/suppliers/filters';
import { SuppliersPage } from '@/features/suppliers/SuppliersPage';

/*
  The supplier register. The search, the status filter, the sort and the page ride the URL, so
  Back from a supplier returns to the list as it was left (ALS-224, the rule ALS-105 set for every
  register), and a filtered view can be shared. `.catch(undefined)` keeps a junk value from
  throwing the route; what the values mean is `suppliersSearchFrom`'s business.
*/
const param = z.string().optional().catch(undefined);

const suppliersSearch = z.object({
    q: param,
    status: param,
    sort: param,
    dir: param,
    page: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/suppliers/')({
    validateSearch: (search) => suppliersSearch.parse(search),
    component: SuppliersRoute,
});

function SuppliersRoute() {
    const raw = Route.useSearch();
    const navigate = useNavigate();

    const search = useMemo(() => suppliersSearchFrom(raw), [raw]);

    const onSearchChange = useCallback(
        (next: SuppliersSearch) =>
            // Replace, so typing a name is not one history entry per keystroke.
            void navigate({
                to: '/suppliers',
                search: suppliersUrlFrom(next),
                replace: true,
                resetScroll: false,
            }),
        [navigate],
    );

    return <SuppliersPage search={search} onSearchChange={onSearchChange} />;
}
